import React, { cloneElement } from 'react'
import ErrorIcon from '../../../components/icons/ErrorIcon'
import { Typography } from '../Typography'
import { useInputGroupContext } from './InputGroup'
import {
  Adornment,
  ErrorText,
  FieldContent,
  FieldLabel,
  FieldWrapper,
  RequiredMark,
} from './InputGroup.styles'
import type { InputGroupFieldProps } from './InputGroup.types'

export const InputGroupField: React.FC<InputGroupFieldProps> = ({
  label,
  labelVisibility = 'hidden',
  error,
  required,
  disabled,
  width,
  grow,
  before,
  after,
  children,
}) => {
  const context = useInputGroupContext()
  const fieldId = React.useId()
  const labelId = label ? `${fieldId}-label` : undefined
  const fieldErrorId = error ? `${fieldId}-error` : undefined

  const isDisabled = disabled ?? context.disabled
  const isRequired = required ?? context.required
  const hasError = !!error

  // Combine aria-describedby IDs
  const describedByIds =
    [fieldErrorId, context.errorId, context.hintId].filter(Boolean).join(' ') || undefined

  // Clone child element with additional props
  const enhancedChild = cloneElement(children, {
    id: children.props.id || fieldId,
    disabled: isDisabled,
    'aria-labelledby': labelId,
    'aria-describedby': describedByIds,
    'aria-invalid': hasError || undefined,
  })

  return (
    <FieldWrapper $width={width} $grow={grow}>
      {label && (
        <FieldLabel id={labelId} $hidden={labelVisibility === 'hidden'}>
          <Typography variant="Heading/Heading-14-Semibold" color="label">
            {label}
            {isRequired && <RequiredMark>*</RequiredMark>}
          </Typography>
        </FieldLabel>
      )}

      <FieldContent $hasBefore={!!before} $hasAfter={!!after}>
        {before && <Adornment>{before}</Adornment>}
        {enhancedChild}
        {after && <Adornment>{after}</Adornment>}
      </FieldContent>

      {error && (
        <ErrorText id={fieldErrorId} variant="Heading/Heading-14-medium" color="danger">
          <ErrorIcon />
          {error}
        </ErrorText>
      )}
    </FieldWrapper>
  )
}
